import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, TextField, MenuItem,
  CircularProgress, Table, TableHead, TableRow,
  TableCell, TableBody, Tooltip, IconButton,
  FormControlLabel, Checkbox,
} from '@mui/material';
import { Refresh as RefreshIcon, Replay as RetryIcon } from '@mui/icons-material';
import { api } from '../../api/rpc';
import { pushError, pushSuccess } from '../../stores/notificationStore';

const MS_PER_HOUR = 3_600_000;
const MS_PER_MINUTE = 60_000;

function msToHHMM(ms: number): string {
  const h = Math.floor(ms / MS_PER_HOUR);
  const m = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function hhmmToMs(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h * 60 + m) * MS_PER_MINUTE;
}

function formatTs(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString();
}

interface DrillEntry {
  drillId: string;
  startDate?: number;
  fromTime?: number;
  untilTime?: number;
  message?: string;
  requestedBy?: string;
  numRetries?: number;
}

export const FireDrillPanel = () => {
  const qc = useQueryClient();
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['sitesAll'],
    queryFn: () => api.getSites(),
    staleTime: 60_000,
  });
  const sites = sitesData?.records ?? [];

  const [siteObjKey, setSiteObjKey] = useState('');

  const { data: historyData, isLoading: histLoading, refetch } = useQuery({
    queryKey: ['fireDrillHistory', siteObjKey],
    queryFn: () => api.getFireDrillHistory(siteObjKey),
    enabled: !!siteObjKey,
    staleTime: 30_000,
  });
  const history: DrillEntry[] = historyData ?? [];

  // Schedule form state
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    date: today,
    fromTime: '08:00',
    untilTime: '17:00',
    numRetries: 3,
    retryTime: 5,
    maxCalls: 10,
    noweekends: false,
    message: '',
  });
  const [scheduling, setScheduling] = useState(false);

  const handleSchedule = async () => {
    if (!siteObjKey) { pushError('Please select a site first.'); return; }
    setScheduling(true);
    try {
      const startDate = new Date(form.date + 'T00:00:00').getTime();
      await api.scheduleFiredrill({
        siteObjKey,
        startDate,
        fromTime: hhmmToMs(form.fromTime),
        untilTime: hhmmToMs(form.untilTime),
        numRetries: form.numRetries,
        retryTime: form.retryTime,
        maxCalls: form.maxCalls,
        noweekends: form.noweekends,
        message: form.message,
      });
      pushSuccess('Fire drill scheduled successfully.');
      await qc.invalidateQueries({ queryKey: ['fireDrillHistory', siteObjKey] });
      refetch();
    } catch (e: unknown) {
      pushError(e instanceof Error ? e.message : 'Failed to schedule fire drill.');
    } finally {
      setScheduling(false);
    }
  };

  const handleRetryAll = async (drillId: string) => {
    if (!siteObjKey) return;
    try {
      await api.retryFireDrillAllFailed(siteObjKey, drillId, false);
      pushSuccess('Retry initiated for all failed.');
    } catch (e: unknown) {
      pushError(e instanceof Error ? e.message : 'Retry failed.');
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Typography variant="h6" sx={{ mb: 2, fontSize: 15, fontWeight: 600 }}>Fire Drill Administration</Typography>

      {/* Site Selector */}
      <TextField
        label="Site" size="small" select sx={{ minWidth: 260, mb: 3 }}
        value={siteObjKey}
        onChange={(e) => setSiteObjKey(e.target.value)}
      >
        {sitesLoading && <MenuItem value=""><em>Loading…</em></MenuItem>}
        {sites.map((s) => (
          <MenuItem key={s.objKey} value={s.objKey}>
            {s.siteId} — {s.name}
          </MenuItem>
        ))}
      </TextField>

      {/* Schedule Form */}
      <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography variant="subtitle1" sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Schedule Fire Drill</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
          <TextField label="Date" type="date" size="small" value={form.date}
            onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} />
          <TextField label="From Time" type="time" size="small" value={form.fromTime}
            onChange={(e) => setForm(f => ({ ...f, fromTime: e.target.value }))} />
          <TextField label="Until Time" type="time" size="small" value={form.untilTime}
            onChange={(e) => setForm(f => ({ ...f, untilTime: e.target.value }))} />
          <TextField label="Retries" type="number" size="small" sx={{ width: 90 }}
            value={form.numRetries}
            onChange={(e) => setForm(f => ({ ...f, numRetries: Number(e.target.value) }))}
            inputProps={{ min: 0, max: 10 }} />
          <TextField label="Retry (min)" type="number" size="small" sx={{ width: 110 }}
            value={form.retryTime}
            onChange={(e) => setForm(f => ({ ...f, retryTime: Number(e.target.value) }))}
            inputProps={{ min: 1, max: 60 }} />
          <TextField label="Max Calls" type="number" size="small" sx={{ width: 100 }}
            value={form.maxCalls}
            onChange={(e) => setForm(f => ({ ...f, maxCalls: Number(e.target.value) }))}
            inputProps={{ min: 1, max: 100 }} />
        </Box>
        <TextField label="Message" size="small" fullWidth multiline minRows={2}
          value={form.message}
          onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
          sx={{ mb: 1 }} />
        <FormControlLabel
          control={<Checkbox size="small" checked={form.noweekends}
            onChange={(e) => setForm(f => ({ ...f, noweekends: e.target.checked }))} />}
          label={<Typography sx={{ fontSize: 12 }}>Skip weekends</Typography>}
        />
        <Box sx={{ mt: 1 }}>
          <Button variant="contained" size="small" onClick={handleSchedule}
            disabled={scheduling || !siteObjKey}>
            {scheduling ? 'Scheduling…' : 'Schedule Fire Drill'}
          </Button>
        </Box>
      </Box>

      {/* History */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontSize: 13, fontWeight: 600, flexGrow: 1 }}>
            Fire Drill History {siteObjKey ? `(${history.length})` : ''}
          </Typography>
          {siteObjKey && (
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetch()}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {!siteObjKey && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>Select a site to view history.</Typography>
        )}
        {siteObjKey && histLoading && <CircularProgress size={16} />}
        {siteObjKey && !histLoading && (
          <Table size="small" sx={{ '& td,& th': { fontSize: 11, py: 0.25 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Scheduled Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Window</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Retries</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requested By</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={6}>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>No fire drills found for this site.</Typography>
                </TableCell></TableRow>
              )}
              {history.map((d) => (
                <TableRow key={d.drillId}>
                  <TableCell>{formatTs(d.startDate ?? 0)}</TableCell>
                  <TableCell>
                    {d.fromTime !== undefined && d.untilTime !== undefined
                      ? `${msToHHMM(d.fromTime)} – ${msToHHMM(d.untilTime)}`
                      : '—'}
                  </TableCell>
                  <TableCell>{d.numRetries ?? 0}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.message || '—'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 10 }}>{d.requestedBy || '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Retry all failed">
                      <IconButton size="small" onClick={() => handleRetryAll(d.drillId)}>
                        <RetryIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
};
